import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import { pendenciasService } from '../services/pendenciasService';
import { FaArrowLeft } from 'react-icons/fa';
import MainLayout from '../../../layouts/MainLayout';
import PendenciaGrupoCard from '../components/PendenciaGrupoCard';
import PendenciasSkeleton from '../components/PendenciasSkeleton';
import PendenciasError from '../components/PendenciasError';
import EmptyPendencias from '../components/EmptyPendencias';

// Uma pendência por documento indeferido — quando o mesmo pedido tem vários
// documentos rejeitados ao mesmo tempo, agrupa tudo num único card
// (PendenciaGrupoCard), em vez de repetir o card do pedido várias vezes.
const agruparPorRequerimento = (pendencias) => {
  const grupos = new Map();

  (pendencias || []).forEach((pendencia) => {
    const chave = pendencia.requerimentoId;

    if (!grupos.has(chave)) {
      grupos.set(chave, {
        requerimentoId: pendencia.requerimentoId,
        numeroRequerimento: pendencia.numeroRequerimento,
        protocoloId: pendencia.protocoloId,
        numeroProtocolo: pendencia.numeroProtocolo,
        nomePedido: pendencia.nomePedido,
        pendencias: []
      });
    }

    grupos.get(chave).pendencias.push(pendencia);
  });

  return Array.from(grupos.values());
};

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const PendenciasContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  animation: ${fadeIn} 0.5s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${colors.lightBlue};

    svg {
      transform: translateX(-3px);
    }
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a2c3e;
  margin-bottom: 8px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const PageSubtitle = styled.p`
  color: #5a6e7c;
  margin-bottom: 32px;
  font-size: 0.9rem;
`;

const PendenciasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PendenciasCount = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #5a6e7c;

  strong {
    color: ${colors.darkBlue};
    margin-right: 4px;
  }
`;

const usePendencias = () => {
  const [pendencias, setPendencias] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPendencias = async () => {
    setLoading(true);
    setError(null);

    const response = await pendenciasService.getPendencias();

    if (response.success) {
      setPendencias(response.data);
    } else {
      setError(response.error || 'Erro ao carregar pendências');
    }

    setLoading(false);
  };

  useEffect(() => {
    loadPendencias();
  }, []);

  // Remove localmente, sem recarregar a lista inteira do servidor — a
  // pendência já foi resolvida no backend pelo próprio POST /documentos/reenviar.
  const removePendencia = (pendenciaId) => {
    setPendencias((atual) => (atual || []).filter((p) => p.pendenciaId !== pendenciaId));
  };

  return { pendencias, loading, error, loadPendencias, removePendencia };
};

const PendenciasPage = () => {
  const navigate = useNavigate();
  const { pendencias, loading, error, loadPendencias, removePendencia } = usePendencias();
  const grupos = useMemo(() => agruparPorRequerimento(pendencias), [pendencias]);

  const handleGoBack = () => navigate('/home');

  // Estado de carregamento: mostra skeleton
  if (loading) {
    return (
      <MainLayout>
        <PageContainer>
          <PendenciasContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Pendências</PageTitle>
            <PageSubtitle>Resolva pendências de pedidos feitos dentro do portal</PageSubtitle>
            <PendenciasSkeleton />
          </PendenciasContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  // Estado de erro: mostra mensagem e botão de tentar novamente
  if (error) {
    return (
      <MainLayout>
        <PageContainer>
          <PendenciasContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Pendências</PageTitle>
            <PageSubtitle>Resolva pendências de pedidos feitos dentro do portal</PageSubtitle>
            <PendenciasError error={error} onRetry={loadPendencias} />
          </PendenciasContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  const hasPendencias = grupos.length > 0;

  // Estado com dados: mostra a lista de pedidos com pendência (um card por
  // requerimento, mesmo quando ele tem mais de um documento indeferido)
  return (
    <MainLayout>
      <PageContainer>
        <PendenciasContainer>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Voltar para Home
          </BackButton>

          <PageTitle>Pendências</PageTitle>
          <PageSubtitle>Resolva pendências de pedidos feitos dentro do portal</PageSubtitle>

          {/* Contador de pendências (apenas se houver itens) */}
          {hasPendencias && (
            <PendenciasCount>
              <strong>{pendencias.length}</strong> documento(s) pendente(s) em <strong>{grupos.length}</strong> pedido(s)
            </PendenciasCount>
          )}

          {/* Lista de cards ou mensagem vazia */}
          <PendenciasList>
            {hasPendencias ? (
              grupos.map((grupo) => (
                <PendenciaGrupoCard key={grupo.requerimentoId} grupo={grupo} onReenviado={removePendencia} />
              ))
            ) : (
              <EmptyPendencias />
            )}
          </PendenciasList>
        </PendenciasContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default PendenciasPage;
